import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FulfillmentFormFields } from '../../../models/Checkout';
import { FulfillmentInfo } from '../../../models/Order';
import { useAppSelector } from '../../../redux/hooks';
import {
  selectContactInfo,
  selectFulfillmentRegionRestrictions,
  selectHeaderOffset,
  selectParams,
  selectSettings,
} from '../../../redux/selectors/checkoutSelectors';
import { FormContainer } from 'react-hook-form-mui';
import { Alert, LoadingButton } from '@mui/lab';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import OptionCard from '../OptionCard';
import AddressForm from '../AddressForm';
import { LocationOn } from '@mui/icons-material';
import AddressContactForm from '../AddressContactForm';
import SavedAddressList from './SavedAddressList';
import useAddressSelection from '../useAddressSelection';
import AddressInfo from '../AddressInfo';
import { RestrictAddressMode } from '../../../models/CheckoutSession';

interface FulfillmentInfoFormProps {
  fulfillmentInfo?: FulfillmentInfo;
  onSubmit: (data: FulfillmentFormFields) => Promise<void>;
  error?: string;
  submitLoading: boolean;
}

export default function FulfillmentInfoForm({
  fulfillmentInfo,
  onSubmit,
  submitLoading,
  error,
}: Readonly<FulfillmentInfoFormProps>) {
  const savedRef = useRef<HTMLDivElement>(null);
  const manualRef = useRef<HTMLDivElement>(null);
  const params = useAppSelector(selectParams);
  const settings = useAppSelector((state) => selectSettings(state, params?.orderType));
  const contactInfo = useAppSelector((state) => selectContactInfo(state, params));
  const { countries, regionDefinitions } = useAppSelector((state) =>
    selectFulfillmentRegionRestrictions(state, params?.orderType),
  );
  const offset = useAppSelector(selectHeaderOffset);

  const formContext = useForm<FulfillmentFormFields>({
    defaultValues: useMemo(() => {
      if (fulfillmentInfo) {
        return fulfillmentInfo;
      } else {
        return { contact: contactInfo };
      }
    }, [fulfillmentInfo, contactInfo]),
  });

  const { reset, handleSubmit } = formContext;

  const [savedSelected, setSavedSelected] = useState<boolean>(false);

  const {
    loadingAddresses,
    loadingRestrictedAddress,
    restrictedAddress,
    hasSavedAddresses,
    addresses,
    onSelectSavedAddress,
  } = useAddressSelection({
    formContext,
    restrictAddressMode: settings.restrictShippingAddressMode,
    restrictAddressId: settings.restrictShippingAddressId,
    lockName: settings.isFulfillmentNameLocked,
    lockEmail: settings.isFulfillmentEmailLocked,
    requirePhone: settings.isFulfillmentPhoneRequired,
    requireCompany: settings.isFulfillmentCompanyRequired,
    restrictedCountries: countries,
    regionDefinitions: regionDefinitions,
  });

  useEffect(() => {
    setSavedSelected(hasSavedAddresses || !!restrictedAddress);
  }, [hasSavedAddresses, restrictedAddress]);

  useEffect(() => {
    if (fulfillmentInfo) {
      return reset(fulfillmentInfo);
    } else {
      return reset({ contact: contactInfo });
    }
  }, [contactInfo, formContext, fulfillmentInfo, reset]);

  const handleSelect = (saved: boolean) => () => {
    setSavedSelected(saved);
    let element: HTMLDivElement | null = null;
    if (saved) {
      element = savedRef.current;
    } else {
      element = manualRef.current;
    }
    setTimeout(() => {
      if (element) {
        const y = (element.getBoundingClientRect().top ?? 0) + window.scrollY - offset - 16;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 500);
  };

  if (loadingRestrictedAddress || loadingAddresses) {
    return (
      <Box m={2} height={300} justifyContent={'center'} display={'flex'} alignItems={'center'}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormContainer formContext={formContext} handleSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {(hasSavedAddresses || restrictedAddress) && (
          <OptionCard
            iconComponent={<LocationOn />}
            label="Saved Address"
            selected={savedSelected}
            onClick={handleSelect(true)}
            ref={savedRef}
          >
            <Stack spacing={2} mb={2}>
              <Box p={2}>
                <AddressContactForm label={'Delivery'} />
              </Box>
              <Divider />
              {hasSavedAddresses && (
                <>
                  <Typography variant="h6" px={2}>
                    Select Address
                  </Typography>
                  <Controller
                    name="savedAddressId"
                    control={formContext.control}
                    render={({ field }) => (
                      <SavedAddressList
                        addresses={addresses ?? []}
                        searchEnabled={true}
                        selectedAddressId={field.value ?? null}
                        onSelectAddress={onSelectSavedAddress}
                      />
                    )}
                  />
                </>
              )}
              <Stack px={2} spacing={2}>
                {restrictedAddress && <AddressInfo hideContact />}
                {error && <Alert severity="error">{error}</Alert>}
                <Stack direction={'row'} justifyContent={'center'}>
                  <LoadingButton
                    variant="contained"
                    size={'large'}
                    loading={submitLoading}
                    type="submit"
                  >
                    Continue
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </OptionCard>
        )}

        {!restrictedAddress && settings.restrictShippingAddressMode === RestrictAddressMode.NONE && (
          <OptionCard
            iconComponent={<LocationOn />}
            label="Enter Address"
            selected={!savedSelected}
            onClick={handleSelect(false)}
            ref={manualRef}
          >
            <Stack m={2} spacing={2}>
              <AddressForm
                label={'Delivery'}
                requirePhone={settings.isFulfillmentPhoneRequired}
                requireCompany={settings.isFulfillmentCompanyRequired}
                companyRename={
                  settings.isFulfillmentCompanyRenameEnabled
                    ? settings.fulfillmentCompanyRenameText
                    : undefined
                }
                restrictName={settings.isFulfillmentNameLocked}
                restrictEmail={settings.isFulfillmentEmailLocked}
                restrictCountries={countries}
                regionDefinitions={regionDefinitions}
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Stack direction={'row'} justifyContent={'center'}>
                <LoadingButton
                  variant="contained"
                  size={'large'}
                  loading={submitLoading}
                  type="submit"
                >
                  Continue
                </LoadingButton>
              </Stack>
            </Stack>
          </OptionCard>
        )}
      </Stack>
    </FormContainer>
  );
}
